/**
 * @fileoverview Custom React Hook for GitHub API
 * @description Hook for managing GitHub API calls with loading and error states
 * @module hooks/useGitHub
 */

import { useState, useCallback } from 'react';
import { githubApi } from '@/services/api/github';

/**
 * Custom hook for GitHub API operations
 * Provides state management for GitHub API calls
 * 
 * @returns {Object} GitHub API methods and state
 */
export const useGitHub = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  /**
   * Fetch branches for a repository
   * @param {string} org - Organization name
   * @param {string} repo - Repository name
   * @returns {Promise<Object|null>} Branches data or null on error
   */
  const fetchBranches = useCallback(async (org, repo) => {
    setLoading(true);
    setError(null);

    try {
      const response = await githubApi.getBranches(org, repo);
      const payload = response?.data ?? response;
      setData(payload);
      return payload;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching branches:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch GitHub Copilot metrics
   * @param {string} org - Organization name
   * @returns {Promise<Object|null>} Copilot metrics or null on error
   */
  const fetchCopilotMetrics = useCallback(async (org) => {
    setLoading(true);
    setError(null);

    try {
      const response = await githubApi.getCopilotMetrics(org);
      // Backend envelope: { success, data: { items: [...] } } or { success, data: {...} }
      const payload = response?.data ?? response;
      const result = Array.isArray(payload) ? payload
        : Array.isArray(payload?.items) ? payload.items
        : payload;
      setData(result);
      return result;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching Copilot metrics:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch repository details
   * @param {string} org - Organization name
   * @param {string} repo - Repository name
   * @returns {Promise<Object|null>} Repository data or null on error
   */
  const fetchRepository = useCallback(async (org, repo) => {
    setLoading(true);
    setError(null);

    try {
      const response = await githubApi.getRepository(org, repo);
      const payload = response?.data ?? response;
      setData(payload);
      return payload;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching repository:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch all organization repositories
   * @param {string} org - Organization name
   * @returns {Promise<Object|null>} Repositories data or null on error
   */
  const fetchOrgRepositories = useCallback(async (org) => {
    setLoading(true);
    setError(null);

    try {
      const response = await githubApi.getOrgRepositories(org);
      const payload = response?.data ?? response;
      setData(payload);
      return payload;
    } catch (err) {
      setError(err.message);
      console.error('Error fetching organization repositories:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check GitHub API health
   * @returns {Promise<Object|null>} Health status or null on error
   */
  const checkHealth = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await githubApi.getHealth();
      setData(response);
      return response;
    } catch (err) {
      setError(err.message);
      console.error('Error checking GitHub API health:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    data,
    fetchBranches,
    fetchCopilotMetrics,
    fetchRepository,
    fetchOrgRepositories,
    checkHealth,
  };
};

export default useGitHub;
